import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Banknote,
  Download,
  Landmark,
  Printer,
  ShieldCheck,
  Smartphone,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePaymentHistory } from '../../application/hooks/usePayments';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useTranslation } from '../../application/hooks/useTranslation';
import { formatBDT } from '../../domain/formatters/currency';
import { formatDate, formatDateTime } from '../../domain/formatters/date';
import { amountInWords } from '../../domain/formatters/amountInWords';
import { PaymentChannel, PaymentStatus } from '../../domain/enums/SystemEnums';
import { QueryState } from '../components/feedback/QueryState';
import { Button } from '../components/ui/Button';
import type { PaymentRecordDto } from '../../domain/contracts/api.contracts';
import './pages.css';
import './Invoice.css';

const CHANNEL_ICON: Record<PaymentChannel, LucideIcon> = {
  [PaymentChannel.BKASH]: Smartphone,
  [PaymentChannel.BANK_DEPOSIT]: Landmark,
  [PaymentChannel.CASH_COUNTER]: Banknote,
  [PaymentChannel.COMMISSION_WALLET]: Wallet,
};

const STATUS_TONE: Record<PaymentStatus, string> = {
  [PaymentStatus.VERIFIED]: 'paid',
  [PaymentStatus.PENDING]: 'pending',
  [PaymentStatus.REJECTED]: 'rejected',
};

/**
 * W11 — money receipt for one PaymentRecordDto (E18 lookup), laid out as an A4 document so
 * the browser's print / "Save as PDF" yields a clean receipt without the app chrome.
 * Falls back to an explicit not-found state instead of a blank page when the id is unknown.
 */
export default function InvoicePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const history = usePaymentHistory({ page: 1, limit: 100 });

  const record = history.data?.items.find((p: PaymentRecordDto) => p.id === id);

  const handlePrint = () => {
    // The document title becomes the suggested PDF file name.
    const previous = document.title;
    if (record) document.title = `Sristi-Green-Valley-Receipt-${record.id}`;
    window.print();
    setTimeout(() => {
      document.title = previous;
    }, 1000);
  };

  return (
    <div data-testid="invoice-page">
      <div className="page-head receipt-toolbar">
        <Link to="/payments" className="receipt-back">
          <ArrowLeft size={16} /> {t('common.back')}
        </Link>
        {record && (
          <div className="receipt-actions">
            <Button variant="secondary" size="sm" onClick={handlePrint} data-testid="print-invoice">
              <Printer size={16} /> {t('common.print')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handlePrint}
              data-testid="download-invoice"
            >
              <Download size={16} /> {t('invoice.download')}
            </Button>
          </div>
        )}
      </div>

      <QueryState
        loading={history.isPending}
        error={history.error}
        onRetry={() => void history.refetch()}
        skeletonCount={3}
      />
      {!history.isPending && !history.error && !record && (
        <div className="state-box state-empty" data-testid="invoice-missing">
          <p>{t('notFound.body')}</p>
          <Link to="/payments">
            <Button variant="secondary">{t('common.back')}</Button>
          </Link>
        </div>
      )}
      {record && (
        <Receipt
          record={record}
          memberName={user?.fullName}
          apartmentId={user?.apartmentId}
          phone={user?.phone}
        />
      )}
    </div>
  );
}

function Receipt({
  record,
  memberName,
  apartmentId,
  phone,
}: {
  record: PaymentRecordDto;
  memberName?: string;
  apartmentId?: string;
  phone?: string;
}) {
  const { t, locale } = useTranslation();
  const tone = STATUS_TONE[record.status] ?? 'pending';
  const ChannelIcon = CHANNEL_ICON[record.channel] ?? Wallet;
  const channelLabel = t(`team.ch_${record.channel}`);
  const isBkash = record.channel === PaymentChannel.BKASH;
  const isBank = record.channel === PaymentChannel.BANK_DEPOSIT;
  const referenceLabel = isBkash
    ? t('invoice.bkashTrx')
    : isBank
      ? t('invoice.bankRef')
      : t('payments.trxId');

  return (
    <article className={`receipt receipt-${tone}`} data-testid="invoice-card" lang={locale}>
      <div className="receipt-band" aria-hidden="true" />

      <div className="receipt-top-center">
        <h1 className="receipt-title">{t('invoice.moneyReceipt')}</h1>
      </div>

      <header className="receipt-header">
        <div className="receipt-brand">
          <img src="/logo.jpeg" alt="" className="receipt-logo" width="48" height="48" />
          <p className="receipt-company">SRISTI GREEN VALLEY LTD.</p>
        </div>
        <div className="receipt-title-block">
          <dl className="receipt-meta">
            <div className="receipt-meta-cell receipt-meta-no">
              <dt>{t('invoice.receiptNo')}</dt>
              <dd className="tabular receipt-id">{record.id}</dd>
            </div>
            <div className="receipt-meta-cell receipt-meta-date">
              <dt>{t('invoice.date')}</dt>
              <dd>{formatDate(record.createdAt, locale)}</dd>
            </div>
          </dl>
        </div>
      </header>

      <section className="receipt-parties">
        <div className="receipt-panel">
          <h2 className="receipt-label">{t('invoice.receivedFrom')}</h2>
          <p className="receipt-party-name">{memberName ?? '—'}</p>
          <dl className="receipt-kv">
            <dt>{t('invoice.accountId')}</dt>
            <dd className="tabular">{apartmentId ?? '—'}</dd>
            <dt>{t('invoice.phone')}</dt>
            <dd className="tabular">{phone ?? '—'}</dd>
          </dl>
        </div>
        <div className="receipt-panel">
          <h2 className="receipt-label">{t('invoice.paymentDetails')}</h2>
          <p className={`receipt-channel receipt-channel-${record.channel.toLowerCase()}`}>
            <ChannelIcon size={15} aria-hidden="true" /> {channelLabel}
          </p>
          <dl className="receipt-kv">
            {record.trxId && (
              <>
                <dt>{referenceLabel}</dt>
                <dd className="tabular receipt-mono">{record.trxId}</dd>
              </>
            )}
            <dt>{t('invoice.paidAt')}</dt>
            <dd>{formatDateTime(record.createdAt, locale)}</dd>
            <dt>{t('installments.status')}</dt>
            <dd>
              <span className={`receipt-status receipt-status-${tone}`}>
                {t(`team.pay_${record.status}`)}
              </span>
            </dd>
          </dl>
        </div>
      </section>

      <table className="receipt-table">
        <thead>
          <tr>
            <th scope="col" className="receipt-col-no">
              #
            </th>
            <th scope="col">{t('invoice.description')}</th>
            <th scope="col" className="receipt-col-amount">
              {t('installments.amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="receipt-col-no">1</td>
            <td>
              <span className="receipt-item">{t('invoice.itemInstallment')}</span>
              <span className="receipt-item-sub tabular">
                {t('invoice.subscriptionRef')}: {record.subscriptionId}
              </span>
            </td>
            <td className="receipt-col-amount tabular">{formatBDT(record.amount, locale)}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} className="receipt-total-label">
              {t('invoice.totalReceived')}
            </td>
            <td className="receipt-col-amount receipt-total tabular">
              {formatBDT(record.amount, locale)}
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="receipt-words">
        <span className="receipt-label">{t('invoice.amountWords')}</span>
        <span className="receipt-words-value">{amountInWords(record.amount)}</span>
      </p>

      <p className={`receipt-note receipt-note-${tone}`}>
        {tone === 'paid' && <ShieldCheck size={16} aria-hidden="true" />}
        {t(`invoice.note_${record.status}`)}
      </p>

      <footer className="receipt-footer">
        <div className="receipt-stamp" aria-hidden="true">
          {t(`invoice.stamp_${record.status}`)}
        </div>
        <div className="receipt-signature">
          <span className="receipt-signature-line" />
          <span>{t('invoice.authorised')}</span>
          <span className="receipt-muted">SRISTI GREEN VALLEY LTD.</span>
        </div>
        <p className="receipt-fineprint">
          {t('invoice.computerGenerated')}
          <br />
          {t('invoice.issuedOn')}: {formatDateTime(new Date().toISOString(), locale)}
        </p>
      </footer>
    </article>
  );
}
